import { useState, useEffect, useCallback } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { generateFakeMeterSeries } from "../fakeData";
import AggregationNotice from "./AggregationNotice";
import PreDataNotice from "./PreDataNotice";
import { readAggregation, buildEnvelopes, pointInterval } from "../seriesAggregation";
import { apiFetch } from "../apiFetch";
import {
  CHART_RANGE_FLOOR,
  RANGE_PRESETS,
  customRangeError,
  preDataNotice,
  windowBounds,
} from "../chartRange";

const POLL_INTERVAL_MS = 60 * 1000;

// Small multiples, one per selected parameter on a shared time axis — the same
// choice WeatherStationChart made in #85, and for the same reason: these
// parameters cannot share a Y axis. Voltage sits near 231, power factor near 0.9
// and energy climbs through the tens, so on a common scale everything but the
// largest collapses onto the baseline.
const PARAMETERS = [
  { key: "voltage_a_v", labelKey: "pmVoltageA", unit: "V", colour: "#ef4444", decimals: 1 },
  { key: "current_a_a", labelKey: "pmCurrentA", unit: "A", colour: "#f59e0b", decimals: 2 },
  { key: "active_power_kw", labelKey: "pmActivePower", unit: "kW", colour: "#2563eb", decimals: 3, signed: true },
  { key: "reactive_power_kvar", labelKey: "pmReactivePower", unit: "kvar", colour: "#7c3aed", decimals: 3, signed: true },
  { key: "apparent_power_kva", labelKey: "pmApparentPower", unit: "kVA", colour: "#0891b2", decimals: 3 },
  { key: "power_factor", labelKey: "pmPowerFactor", unit: "", colour: "#10b981", decimals: 3 },
  { key: "energy_total_kwh", labelKey: "pmEnergyTotal", unit: "kWh", colour: "#65a30d", decimals: 2, cumulative: true },
  { key: "energy_forward_kwh", labelKey: "pmEnergyForward", unit: "kWh", colour: "#16a34a", decimals: 2, cumulative: true },
  { key: "energy_reverse_kwh", labelKey: "pmEnergyReverse", unit: "kWh", colour: "#dc2626", decimals: 2, cumulative: true },
  { key: "reactive_energy_forward_kvarh", labelKey: "pmReactiveEnergyForward", unit: "kvarh", colour: "#9333ea", decimals: 2, cumulative: true },
  { key: "reactive_energy_reverse_kvarh", labelKey: "pmReactiveEnergyReverse", unit: "kvarh", colour: "#c026d3", decimals: 2, cumulative: true },
  // Per-phase quantities. Offered only when the API reports them, which on a
  // one-phase install it does not — see available_fields in routes/meter.mjs.
  { key: "voltage_b_v", labelKey: "pmVoltageB", unit: "V", colour: "#f97316", decimals: 1 },
  { key: "voltage_c_v", labelKey: "pmVoltageC", unit: "V", colour: "#84cc16", decimals: 1 },
  { key: "current_b_a", labelKey: "pmCurrentB", unit: "A", colour: "#eab308", decimals: 2 },
  { key: "current_c_a", labelKey: "pmCurrentC", unit: "A", colour: "#a3e635", decimals: 2 },
  { key: "active_power_a_kw", labelKey: "pmActivePowerA", unit: "kW", colour: "#3b82f6", decimals: 3, signed: true },
  { key: "active_power_b_kw", labelKey: "pmActivePowerB", unit: "kW", colour: "#6366f1", decimals: 3, signed: true },
  { key: "active_power_c_kw", labelKey: "pmActivePowerC", unit: "kW", colour: "#8b5cf6", decimals: 3, signed: true },
  { key: "reactive_power_a_kvar", labelKey: "pmReactivePowerA", unit: "kvar", colour: "#a855f7", decimals: 3, signed: true },
  { key: "apparent_power_a_kva", labelKey: "pmApparentPowerA", unit: "kVA", colour: "#06b6d4", decimals: 3 },
  { key: "power_factor_a", labelKey: "pmPowerFactorA", unit: "", colour: "#059669", decimals: 3 },
  { key: "voltage_ab_v", labelKey: "pmVoltageAB", unit: "V", colour: "#e11d48", decimals: 1 },
  { key: "voltage_bc_v", labelKey: "pmVoltageBC", unit: "V", colour: "#be123c", decimals: 1 },
  { key: "voltage_ca_v", labelKey: "pmVoltageCA", unit: "V", colour: "#9f1239", decimals: 1 },
];

// Opening with all eleven would be a wall of charts. Voltage, current and active
// power are the three an operator reads first on a line.
const DEFAULT_SELECTED = ["voltage_a_v", "current_a_a", "active_power_kw"];

function formatTick(iso, range) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  // A 24h range is read by time of day; anything longer by date.
  return range === "24h"
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

const clockTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// Reads the point rather than payload[0].value, because an aggregated panel
// renders two series (the min/max band and the mean line) and the first of them
// is the band's [lo, hi] pair.
//
// The tooltip is where the aggregation stops being implied: a point covering 27
// readings shows the interval it covers and the extremes inside it, instead of
// asserting one value at one instant that nothing was ever measured at (#108).
function ParameterTooltip({ active, payload, label, param, t }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  const value = point?.[param.key];
  if (value === undefined || value === null) return null;

  const fmt = (v) => {
    const shown = Number(v).toFixed(param.decimals);
    return param.signed && Number(v) > 0 ? `+${shown}` : shown;
  };
  const interval = pointInterval(point);
  const low = point[`${param.key}_min`];
  const high = point[`${param.key}_max`];

  return (
    <div style={styles.tooltip}>
      <div style={styles.tooltipTime}>
        {interval
          ? t("aggIntervalTooltip", {
              n: interval.n,
              start: clockTime(interval.start),
              end: clockTime(interval.end),
            })
          : new Date(label).toLocaleString("en-GB")}
      </div>
      <div style={styles.tooltipValue}>
        {t(param.labelKey)}:{" "}
        <strong>
          {fmt(value)}
          {param.unit && ` ${param.unit}`}
        </strong>
      </div>
      {typeof low === "number" && typeof high === "number" && (
        <div style={styles.tooltipRange}>
          {t("aggRangeTooltip", { min: fmt(low), max: fmt(high) })}
        </div>
      )}
      {point.is_latest && <div style={styles.tooltipRange}>{t("aggLatestTooltip")}</div>}
    </div>
  );
}

// `simulated` renders generated data and makes no network request — every tier
// below superuser is simulated, and fetching here would put real site
// measurements in front of them.
export default function PowerMeterChart({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [range, setRange] = useState("24h");
  const [data, setData] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customError, setCustomError] = useState(null);
  const [committedCustom, setCommittedCustom] = useState({ from: "", to: "" });
  // "auto" lets the API decide the point budget; "raw" asks for the readings as
  // received, which the API serves or refuses with raw_capped (#108).
  const [plotMode, setPlotMode] = useState("auto");
  const [agg, setAgg] = useState(() => readAggregation(null));
  // When this meter started reporting, from the API (#184).
  const [firstReading, setFirstReading] = useState(null);

  // Same options and same custom-range rules as TowerDriftChart and
  // WeatherStationChart, so none of the three disagree about what "7d" means.
  // From the shared list so all four charts offer the same set (#231).
  const RANGE_OPTIONS = RANGE_PRESETS.map(({ key, value }) => ({ label: t(key), value }));

  const toggleParameter = (key) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const buildUrl = () => {
    const base = `${apiBaseUrl}/api/meter/series?device_id=${encodeURIComponent(deviceId)}`;
    // Only appended when raw was actually asked for, so the default request is
    // byte-for-byte the one this endpoint has always received.
    const raw = plotMode === "raw" ? "&raw=1" : "";
    if (range === "custom" && committedCustom.from && committedCustom.to) {
      return `${base}&start=${committedCustom.from}&end=${committedCustom.to}${raw}`;
    }
    return `${base}&range=${range}${raw}`;
  };

  // Custom is selected but Apply has not been pressed on a complete pair of dates
  // yet, so there is no range to ask for. buildUrl() would fall through to
  // `range=custom`, and meter.mjs rejects that with a 400 — the fault #91 fixed in
  // TowerDriftChart and #85 had to fix again in WeatherStationChart. The issue was
  // explicit that it must not be inherited a third time, so the guard is carried
  // over rather than the range selector being copied without it.
  //
  // As in WeatherStationChart this is qualified by !simulated: there is no request
  // to reject in simulated mode, and the generator draws a custom range as 30d so
  // the picker still does something for a non-superuser.
  const customPending = range === "custom" && !(committedCustom.from && committedCustom.to);
  const blockFetch = customPending && !simulated;

  // The pilot meter's first reading is 2026-08-18, so a full-range window is
  // mostly blank for it. Unlabelled, that blank reads as an outage (#184).
  const windowStart = windowBounds(range, committedCustom);
  const preData = preDataNotice(firstReading, windowStart.from, windowStart.to);

  const fetchData = useCallback(async () => {
    if (blockFetch) { setError(null); setLoading(false); return; }
    try {
      let json;
      if (simulated) {
        json = generateFakeMeterSeries(deviceId, range === "custom" ? "30d" : range);
      } else {
        const res = await apiFetch(buildUrl());
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      // The min/max envelope is turned into the [lo, hi] pairs the band renders
      // from once, here, rather than per panel on every draw.
      setData(buildEnvelopes(json.data ?? [], PARAMETERS.map((p) => p.key)));
      setAvailableFields(json.available_fields ?? json.fields ?? []);
      // Simulated data describes no real deployment, so it must not claim an
      // install date.
      setFirstReading(simulated ? null : (json.first_reading ?? null));
      setAgg(readAggregation(json));
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, range, committedCustom, blockFetch, simulated, plotMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // No poll either, or the interval would fire the same rejected request every
    // POLL_INTERVAL_MS while the user is still filling in the dates.
    if (blockFetch) { setLoading(false); return undefined; }
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, blockFetch]);

  // Only offer a parameter the range actually contains, so toggling one on can
  // never produce an empty chart. This is also what keeps phases B and C out of
  // the selector on a one-phase install without hardcoding a phase count here.
  const offered = PARAMETERS.filter(
    (p) => availableFields.length === 0 || availableFields.includes(p.key)
  );
  const shown = offered.filter((p) => selected.includes(p.key));

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{t("pmHistoryTitle")}</h2>
          <p style={styles.subtitle}>{label ?? deviceId}</p>
        </div>
      </div>

      {/* Range selector — same set as the drift and station charts */}
      <div style={styles.rangeRow}>
        {RANGE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setRange(opt.value)}
            style={range === opt.value ? styles.rangeActive : styles.rangeBtn}>
            {opt.label}
          </button>
        ))}
        <button onClick={() => setRange("custom")}
          style={range === "custom" ? styles.rangeActive : styles.rangeBtn}>
          {t("rangeCustom")}
        </button>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {range === "custom" && (
        <div style={styles.customRangeRow}>
          <label style={styles.customLabel}>{t("customFrom")}</label>
          <input type="date" value={customFrom}
            min={CHART_RANGE_FLOOR}
            max={customTo || new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomFrom(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          {/* One click back to the start of the data (#231). Sets the field and
              stops there rather than applying, so a long window is never
              requested before the operator can see what it chose. */}
          <button type="button" style={styles.fromStartBtn}
            title={t("fromStartTitle")}
            onClick={() => { setCustomFrom(CHART_RANGE_FLOOR); setCustomError(null); }}>
            {t("fromStart")}
          </button>
          <label style={styles.customLabel}>{t("customTo")}</label>
          <input type="date" value={customTo} min={customFrom || CHART_RANGE_FLOOR}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomTo(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          <button style={styles.applyBtn} onClick={() => {
            const key = customRangeError(customFrom, customTo);
            if (key) { setCustomError(t(key)); return; }
            setCustomError(null);
            setCommittedCustom({ from: customFrom, to: customTo });
          }}>
            {t("apply")}
          </button>
          {customError && <span style={styles.customError}>{customError}</span>}
        </div>
      )}

      {/* Parameter selector. Checkboxes rather than tabs so several can be shown
          at once, matching the other two charts. */}
      <div style={styles.paramToggles}>
        {offered.map((p) => (
          <label key={p.key} style={styles.paramToggle}>
            <input type="checkbox" checked={selected.includes(p.key)}
              onChange={() => toggleParameter(p.key)} />
            <span style={{ ...styles.swatch, background: p.colour }} />
            <span style={{ marginLeft: 2 }}>{t(p.labelKey)}</span>
          </label>
        ))}
      </div>

      {/* Why the chart is blank before a given date, when it is. Not gated on
          data.length: the case most in need of explaining is the empty one. */}
      {!loading && !error && !customPending && (
        <PreDataNotice notice={preData} t={t} />
      )}

      {/* What the points are, and the raw/aggregated control. Renders nothing
          when the series was not decimated. */}
      {!loading && !error && !customPending && data.length > 0 && (
        <AggregationNotice
          agg={agg}
          mode={plotMode}
          onModeChange={setPlotMode}
          t={t}
          canToggle={!simulated}
        />
      )}

      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : customPending ? (
        // Not "No data" — nothing has been asked for yet. Saying no data exists
        // for a range the user has not finished specifying is a false statement
        // about the meter.
        <div style={styles.placeholder}>{t("enterBothDates")}</div>
      ) : data.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : shown.length === 0 ? (
        <div style={styles.placeholder}>{t("pmNoParameterSelected")}</div>
      ) : (
        <div>
          {shown.map((p) => (
            <div key={p.key} style={styles.panel}>
              <div style={styles.panelHead}>
                <span style={{ ...styles.swatch, background: p.colour }} />
                <span style={styles.panelLabel}>{t(p.labelKey)}</span>
                {p.unit && <span style={styles.panelUnit}>{p.unit}</span>}
                {p.cumulative && <span style={styles.cumulativeTag}>{t("pmCumulative")}</span>}
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <ComposedChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="ts"
                    tickFormatter={(v) => formatTick(v, range)}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    minTickGap={40}
                    stroke="#e5e7eb"
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    width={54}
                    stroke="#e5e7eb"
                  />
                  <Tooltip content={<ParameterTooltip param={p} t={t} />} />
                  {/* Active and reactive power cross zero on this meter, and the
                      crossing is the interesting part — without a marked axis a
                      reader cannot see which side of it a curve is on. */}
                  {p.signed && <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="2 2" />}
                  {/* The min/max envelope of each aggregated point, drawn under
                      the line. This is the fix for the reported symptom: a load
                      switching inside one bucket used to average to a current
                      that never flowed, with the step edge gone. The band is
                      what actually happened in the interval; the line is the
                      trend through it. Absent on a raw series and on cumulative
                      registers, where the API sends no envelope at all. */}
                  <Area dataKey={`${p.key}_band`} stroke="none" fill={p.colour}
                    fillOpacity={0.16} connectNulls={false} activeDot={false}
                    isAnimationActive={false} />
                  {/* connectNulls={false} is what turns a gap in the data into a
                      gap in the line. A missing uplink must read as absent, not
                      as a plunge to the axis — on a line meter that would look
                      like a supply failure (#33/#57). */}
                  <Line type="monotone" dataKey={p.key} stroke={p.colour}
                    dot={false} strokeWidth={1.5} connectNulls={false}
                    isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      <p style={styles.footnote}>{t("pmChartUnverifiedNote")}</p>
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif", maxWidth: 1600 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  rangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  rangeBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13 },
  rangeActive: { padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  customRangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  customLabel: { fontSize: 12, color: "#6b7280" },
  dateInput: { padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#374151", background: "#f9fafb" },
  applyBtn: { padding: "4px 14px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  // Quieter than applyBtn on purpose: this fills a field, it does not run the
  // query, and it must not compete with Apply for the eye (#231).
  fromStartBtn: { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 12 },
  customError: { fontSize: 12, color: "#ef4444" },
  paramToggles: { display: "flex", gap: 14, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  paramToggle: { display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 13, color: "#374151", userSelect: "none" },
  swatch: { display: "inline-block", width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
  panel: { borderTop: "1px solid #f3f4f6", paddingTop: 8, marginBottom: 4 },
  panelHead: { display: "flex", alignItems: "center", gap: 6, marginBottom: 2 },
  panelLabel: { fontSize: 13, fontWeight: 600, color: "#374151" },
  panelUnit: { fontSize: 11, color: "#9ca3af" },
  cumulativeTag: { fontSize: 10, color: "#6b7280", background: "#f3f4f6", padding: "1px 6px", borderRadius: 8 },
  placeholder: { height: 140, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  tooltip: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  tooltipTime: { fontSize: 11, color: "#9ca3af", marginBottom: 2 },
  tooltipValue: { fontSize: 12, color: "#374151" },
  tooltipRange: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  footnote: { marginTop: 12, marginBottom: 0, fontSize: 11, color: "#9ca3af", lineHeight: 1.5 },
};
